import { useCallback, useEffect, useRef, useState } from 'react';
import {
  PSX_MEMCARD_BYTES,
  createBlankPsxMemcard,
  deletePsxMemcardSave,
  formatPsxMemcardUsage,
  inspectPsxMemcard,
  listPsxMemcardSaves,
  loadSram,
  psxMemcardSaveKey,
  repairPsxMemcard,
  saveSram,
  type PsxMemcardMeta,
  type PsxMemcardSave,
  type PsxMemcardUsage,
} from '../storage/saveRepository';
import { requestSync } from '../storage/cloudSync';

export interface PsxMemcardPanelProps {
  cards: PsxMemcardMeta[];
  activeId: string;
  onSelect: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onDeleteCard: (id: string) => void;
  onAddCard: () => void;
  canAdd: boolean;
  addDisabledReason?: string;
  toast: (message: string, tone?: 'info' | 'success' | 'error') => void;
}

type CardDetail = {
  usage: PsxMemcardUsage | null;
  saves: PsxMemcardSave[];
};

export function PsxMemcardPanel({
  cards,
  activeId,
  onSelect,
  onRename,
  onDeleteCard,
  onAddCard,
  canAdd,
  addDisabledReason,
  toast,
}: PsxMemcardPanelProps) {
  const [details, setDetails] = useState<Record<string, CardDetail>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const importCardId = useRef<string | null>(null);
  const importInput = useRef<HTMLInputElement>(null);

  const reload = useCallback(() => {
    void Promise.all(
      cards.map(async (card) => {
        let data = await loadSram(psxMemcardSaveKey(card.id));
        if (data) {
          const repaired = repairPsxMemcard(data);
          if (repaired?.changed) {
            data = repaired.data;
            await saveSram(psxMemcardSaveKey(card.id), data);
            requestSync();
          }
        }
        return [
          card.id,
          {
            usage: inspectPsxMemcard(data ?? null),
            saves: listPsxMemcardSaves(data ?? null),
          } satisfies CardDetail,
        ] as const;
      }),
    ).then((entries) => setDetails(Object.fromEntries(entries)));
  }, [cards]);

  useEffect(() => {
    reload();
  }, [reload]);

  const mutateCard = async (cardId: string, next: Uint8Array, okMessage: string) => {
    await saveSram(psxMemcardSaveKey(cardId), next);
    requestSync();
    reload();
    toast(okMessage, 'success');
  };

  return (
    <div className="space-y-2">
      <ul className="space-y-2">
        {cards.map((card) => {
          const active = card.id === activeId;
          const detail = details[card.id];
          const usage = detail?.usage;
          const usageLabel =
            !detail
              ? '確認中…'
              : detail.usage === null
                ? '未作成（起動時に空カードを用意）'
                : formatPsxMemcardUsage(detail.usage);
          const usageTone =
            usage && usage.formatted && usage.freeBlocks === 0
              ? 'text-amber-300'
              : usage && !usage.formatted
                ? 'text-red-300'
                : 'text-slate-500';
          const expanded = expandedId === card.id;
          const saves = detail?.saves ?? [];

          return (
            <li
              key={card.id}
              className={`rounded-xl border px-3 py-2 ${
                active ? 'border-accent/50 bg-accent/10' : 'border-base-600 bg-base-800/60'
              }`}
            >
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => onSelect(card.id)}
                >
                  <span className="block truncate text-sm font-medium text-slate-100">{card.name}</span>
                  <span className={`block text-[0.65rem] ${usageTone}`}>{usageLabel}</span>
                  <span className="text-[0.65rem] text-slate-500">
                    {active ? '使用中（次の起動から）' : 'タップで選択'}
                    {detail && usage?.formatted ? ` · セーブ ${saves.length} 件` : ''}
                  </span>
                </button>
                <button
                  type="button"
                  className="btn-ghost !min-h-9 !px-2 text-xs"
                  onClick={() => setExpandedId(expanded ? null : card.id)}
                >
                  {expanded ? '閉じる' : '中身'}
                </button>
                <button
                  type="button"
                  className="btn-ghost !min-h-9 !px-2 text-xs"
                  onClick={() => {
                    const name = window.prompt('カード名', card.name);
                    if (!name?.trim()) return;
                    onRename(card.id, name.trim());
                  }}
                >
                  改名
                </button>
                <button
                  type="button"
                  className="btn-ghost !min-h-9 !px-2 text-xs text-red-300"
                  disabled={cards.length <= 1}
                  onClick={() => {
                    if (cards.length <= 1) return;
                    if (!window.confirm(`「${card.name}」を削除しますか？中のセーブも消えます。`)) return;
                    onDeleteCard(card.id);
                  }}
                >
                  削除
                </button>
              </div>

              {expanded && (
                <div className="mt-2 space-y-2 border-t border-base-600/80 pt-2">
                  {!detail ? (
                    <p className="text-xs text-slate-500">読み込み中…</p>
                  ) : detail.usage === null ? (
                    <div className="space-y-2">
                      <p className="text-xs text-slate-400">
                        まだカード画像がありません。空カードを作るか、`.mcd` / `.mcr` を読み込めます。
                      </p>
                      <button
                        type="button"
                        className="btn-ghost w-full text-xs"
                        onClick={() => {
                          void mutateCard(card.id, createBlankPsxMemcard(), '空のメモリーカードを用意しました');
                        }}
                      >
                        空カードを作る
                      </button>
                      <button
                        type="button"
                        className="btn-ghost w-full text-xs"
                        onClick={() => {
                          importCardId.current = card.id;
                          importInput.current?.click();
                        }}
                      >
                        ファイルから読み込む
                      </button>
                    </div>
                  ) : !detail.usage.formatted ? (
                    <div className="space-y-2">
                      <p className="text-xs text-red-300">未フォーマットです。空のカードに初期化できます。</p>
                      <button
                        type="button"
                        className="btn-ghost w-full text-xs"
                        onClick={() => {
                          if (!window.confirm('このカードを空のフォーマット済み状態に初期化しますか？')) return;
                          void mutateCard(card.id, createBlankPsxMemcard(), 'カードを初期化しました');
                        }}
                      >
                        フォーマット（空にする）
                      </button>
                    </div>
                  ) : (
                    <>
                      {saves.length === 0 ? (
                        <p className="text-xs text-slate-400">
                          セーブはまだありません（空き {detail.usage.freeBlocks}/{detail.usage.totalBlocks}）。
                        </p>
                      ) : (
                        <ul className="space-y-1.5">
                          {saves.map((save) => (
                            <li
                              key={`${card.id}-${save.firstBlock}`}
                              className="flex items-start gap-2 rounded-lg bg-base-900/70 px-2.5 py-2"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm text-slate-100">{save.title}</p>
                                <p className="truncate text-[0.65rem] text-slate-500">
                                  {save.filename} · {save.blocks} ブロック
                                </p>
                              </div>
                              <button
                                type="button"
                                className="btn-ghost !min-h-8 shrink-0 !px-2 text-xs text-red-300"
                                onClick={() => {
                                  if (!window.confirm(`「${save.title}」を削除しますか？`)) return;
                                  void (async () => {
                                    const data = await loadSram(psxMemcardSaveKey(card.id));
                                    if (!data) {
                                      toast('カードデータを読めませんでした', 'error');
                                      return;
                                    }
                                    const next = deletePsxMemcardSave(data, save.firstBlock);
                                    if (!next) {
                                      toast('削除に失敗しました', 'error');
                                      return;
                                    }
                                    await mutateCard(card.id, next, 'セーブを削除しました');
                                  })();
                                }}
                              >
                                削除
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <button
                        type="button"
                        className="btn-ghost w-full text-xs"
                        onClick={() => {
                          importCardId.current = card.id;
                          importInput.current?.click();
                        }}
                      >
                        ファイルから読み込む
                      </button>
                      <button
                        type="button"
                        className="btn-ghost w-full text-xs text-red-300/90"
                        onClick={() => {
                          if (
                            !window.confirm(
                              `「${card.name}」を空のカードに戻しますか？すべてのセーブが消えます。`,
                            )
                          ) {
                            return;
                          }
                          void mutateCard(card.id, createBlankPsxMemcard(), 'カードを空にしました');
                        }}
                      >
                        すべて消して空カードにする
                      </button>
                    </>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <input
        ref={importInput}
        type="file"
        accept=".mcd,.mcr,.bin,.memcard"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          const cardId = importCardId.current;
          e.target.value = '';
          importCardId.current = null;
          if (!file || !cardId) return;
          void (async () => {
            const buf = new Uint8Array(await file.arrayBuffer());
            if (buf.byteLength !== PSX_MEMCARD_BYTES) {
              toast(
                `PS1 カードは ${PSX_MEMCARD_BYTES} バイトです（実際は ${buf.byteLength}）`,
                'error',
              );
              return;
            }
            await mutateCard(cardId, buf, `${file.name} を取り込みました`);
          })();
        }}
      />

      <button type="button" className="btn-ghost w-full" disabled={!canAdd} onClick={onAddCard}>
        {canAdd ? '空のメモリーカードを追加' : (addDisabledReason ?? '追加できません')}
      </button>
    </div>
  );
}
